import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Sparkles, Trophy, BookOpen, ArrowRight, Zap, Target, Plus } from "lucide-react";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { log } from "../lib/logger";

const RANK_BG = ["bg-brand-yellow", "bg-brand-mint", "bg-brand-pink text-white"];
const rankBgFor = (i) => RANK_BG[i] || "bg-white";

const StatCard = ({ icon: Icon, value, label, bg, testId }) => (
  <div data-testid={testId} className={`brutal-card p-6 ${bg}`}>
    <Icon className="w-8 h-8 text-brand-navy mb-3" strokeWidth={2.5} />
    <div className="font-display text-4xl font-bold text-brand-navy">{value}</div>
    <div className="text-sm font-bold uppercase text-brand-navy/70 tracking-wide mt-1">{label}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ due: 0, words_learned: 0, enrolled_count: 0, total_reviews: 0, today_xp: 0, today_reviews: 0 });
  const [myCourses, setMyCourses] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, mine, all, lb] = await Promise.all([
          api.get("/stats"),
          api.get("/courses/mine"),
          api.get("/courses"),
          api.get("/leaderboard"),
        ]);
        setStats(s.data);
        setMyCourses(mine.data);
        const mineIds = new Set(mine.data.map((c) => c.id));
        setSuggested(all.data.filter((c) => !mineIds.has(c.id)).slice(0, 3));
        setTop(lb.data.slice(0, 5));
      } catch (err) {
        log.error("Dashboard load failed", err);
      }
    })();
    // Dashboard loads once on mount; api is a module-level constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In-app streak reminder — nudge if streak > 0 and no review today
  useEffect(() => {
    if (!user || !stats) return;
    if ((user.streak || 0) > 0 && (stats.today_reviews || 0) === 0) {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          new Notification("🔥 Keep your streak alive!", {
            body: `You're on a ${user.streak}-day streak. Just 5 minutes of practice keeps it going.`,
            tag: "lingofacil-streak",
          });
        } catch (err) {
          log.error("Streak notification failed", err);
        }
      }
    }
  }, [user, stats]);

  const goal = user?.daily_goal || 50;
  const todayXp = stats.today_xp || 0;
  const goalPct = Math.min(100, Math.round((todayXp / goal) * 100));

  return (
    <div className="min-h-screen grain-bg bg-brand-peach" data-testid="dashboard-page">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">
            Hey {user?.name?.split(" ")[0] || "there"} 👋
          </h1>
          <p className="text-brand-navy/70 text-lg mt-2">Ready to keep the streak alive?</p>
        </motion.div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <StatCard icon={Flame} value={user?.streak || 0} label="Day streak" bg="bg-brand-pink/40" testId="stat-streak" />
          <StatCard icon={Sparkles} value={user?.xp || 0} label="Total XP" bg="bg-brand-yellow" testId="stat-xp" />
          <StatCard icon={BookOpen} value={stats.words_learned} label="Words learned" bg="bg-brand-mint/50" testId="stat-words" />
          <StatCard icon={Target} value={stats.due} label="Cards due" bg="bg-white" testId="stat-due" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Daily goal + review CTA */}
          <div className="lg:col-span-2 space-y-6">
            <div className="brutal-card p-8 bg-white">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="brutal-badge bg-brand-mint mb-3">
                    <Zap className="w-3.5 h-3.5" strokeWidth={3} /> Daily goal
                  </div>
                  <h2 className="font-display text-3xl font-bold text-brand-navy">
                    {todayXp} / {goal} XP today
                  </h2>
                  <p className="text-brand-navy/70 mt-1">Small steps, big streaks.</p>
                </div>
                <Link
                  to={stats.due > 0 ? "/review" : "/courses"}
                  data-testid="dashboard-review-cta"
                  className="brutal-btn-pink text-lg"
                >
                  {stats.due > 0 ? `Review ${stats.due} due` : "Start learning"}
                  <ArrowRight className="w-5 h-5" strokeWidth={3} />
                </Link>
              </div>
              <div className="mt-6 h-6 border-2 border-brand-ink rounded-full bg-brand-peach overflow-hidden">
                <div
                  className="h-full bg-brand-mint border-r-2 border-brand-ink transition-all"
                  style={{ width: `${goalPct}%` }}
                />
              </div>
            </div>

            {/* My courses */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-bold text-brand-navy">My courses</h2>
                <Link to="/courses" className="text-sm font-bold text-brand-navy/70 hover:text-brand-pink underline">
                  Browse all →
                </Link>
              </div>
              {myCourses.length === 0 ? (
                <div className="brutal-card p-8 text-center bg-white">
                  <p className="text-brand-navy/70 mb-4">You haven't enrolled in a course yet.</p>
                  <Link to="/courses" className="brutal-btn-yellow inline-flex">
                    Pick your first language
                  </Link>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {myCourses.map((c) => (
                    <Link
                      key={c.id}
                      to={`/learn/${c.id}`}
                      data-testid={`dashboard-course-${c.id}`}
                      className="brutal-card-sm p-5 hover:-translate-y-1 transition-transform bg-white"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="w-16 h-16 rounded-xl border-2 border-brand-ink flex items-center justify-center text-3xl shrink-0"
                          style={{ backgroundColor: c.color || "#FFD166" }}
                        >
                          {c.flag}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-lg text-brand-navy truncate">{c.title}</div>
                          <div className="text-sm text-brand-navy/70">
                            {c.progress?.learned || 0} / {c.card_count} learned
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 h-2 border-2 border-brand-ink rounded-full bg-brand-peach overflow-hidden">
                        <div
                          className="h-full bg-brand-mint"
                          style={{ width: `${c.progress?.pct || 0}%` }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Suggested */}
            {suggested.length > 0 && (
              <div>
                <h2 className="font-display text-2xl font-bold text-brand-navy mb-4">Try something new</h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  {suggested.map((c) => (
                    <Link
                      key={c.id}
                      to={`/courses/${c.id}`}
                      className="brutal-card-sm p-5 hover:-translate-y-1 transition-transform bg-white text-center"
                    >
                      <div
                        className="w-14 h-14 mx-auto rounded-xl border-2 border-brand-ink flex items-center justify-center text-2xl mb-3"
                        style={{ backgroundColor: c.color || "#FFD166" }}
                      >
                        {c.flag}
                      </div>
                      <div className="font-display font-bold text-brand-navy">{c.title}</div>
                      <div className="text-xs text-brand-navy/60 mt-1">{c.card_count} cards</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {user?.is_admin && (
              <Link to="/create" data-testid="dashboard-create-cta" className="block brutal-card p-6 bg-brand-mint hover:-translate-y-1 transition-transform">
                <Plus className="w-10 h-10 text-brand-navy mb-3" strokeWidth={2.5} />
                <div className="font-display text-2xl font-bold text-brand-navy">Create a deck</div>
                <p className="text-brand-navy/80 mt-1 text-sm">Manual or AI-generated. Your call.</p>
              </Link>
            )}

            <div className="brutal-card p-6 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold text-xl text-brand-navy flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-brand-yellow" strokeWidth={3} />
                  Top learners
                </div>
                <Link to="/leaderboard" className="text-xs font-bold text-brand-navy/60 hover:text-brand-pink underline">
                  View all
                </Link>
              </div>
              <div className="space-y-2">
                {top.map((u, i) => (
                  <div key={u.id} className="flex items-center gap-3 py-1">
                    <div className={`w-8 h-8 rounded-full border-2 border-brand-ink flex items-center justify-center font-bold text-sm ${rankBgFor(i)}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 truncate font-bold text-brand-navy">{u.name}</div>
                    <div className="text-sm font-bold text-brand-navy/70">{u.xp} XP</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
